import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useBreakpoints } from "@narmi/design_system";
import { entities } from "byzantine";
import Feature from "byzantine/src/Feature";

import Account from "byzantine/src/Account";

import { useDashboardLayoutRedesignEnabled } from "byzantine/src/dbbl/businessLogic/modules/dashboard/hooks";
import NarmiTiles from "../../src/modules/narmiTiles";
import NpsSurvey from "../../src/modules/nps";
import PageLayout from "../../src/components/PageLayout";
import DashboardHeader from "./DashboardHeader";
import ChartCard from "./ChartCard";
import ServicesCard from "./ServicesCard";
import AccountTotalsCard from "./AccountTotals/AccountTotalsCard";
import AccountBalances from "./AccountBalances";
import MarketingCardContainer from "./MarketingCardContainer";
import OnboardingCard from "./OnboardingCard";
import SavvyMoneyWidget from "../savvy_money/SavvyMoneyWidget";
import MarketingImage from "./MarketingImage";
import DashboardDialog from "./DashboardDialog";
import useIsDashboardDialogOpen from "./useIsDashboardDialogOpen";
import SubscriptionCard from "../subscriptions/SubscriptionCard";
import CardOffers from "./CardOffers";
import ExternalAccountTiles from "./ExternalAccountTiles";
import type { Institution } from "../../types";
import ArrayPlusWidget from "../ArrayPlus/ArrayPlusWidget";
import DashboardCustomization from "./DashboardCustomization/DashboardCustomization";
import { useVisibleWebDashboardSections } from "./DashboardCustomization/hooks/hooks";
import {
  renderLeftSection,
  renderRightSection,
  RecentTransactionsSection,
} from "./Dashboard.sections";
import { MARKETING_CARD_IMAGE_ADDRESS } from "./constants";
import type { DashboardSectionContext } from "./types";
import { useAccountsContext } from "../contexts/AccountContext";
import useSettledTransactions from "./RecentTransactionsModule/useSettledTransactions";

import "./Dashboard.scss";
import useIsSubscriptionManagementEnabled from "../subscriptions/SubscriptionCard/hooks";
import ExternalAccountsContainer from "../../src/modules/dashboard/ExternalAccounts/ExternalAccountsContainer/ExternalAccountsContainer";
import AccountsModule from "../../src/modules/accounts/AccountsModule";
import LargeViewportLayout from "./DashboardLayout/LargeViewportLayout";
import QuickTransferCard from "./QuickTransferCard";
import BalanceChart from "./Charts/BalanceChart";
import NetWorthWidgetCard from "./NetWorthWidget/NetWorthWidgetCard";

interface DashboardProps {
  androidUrl: string;
  appleUrl: string;
  institution: Institution;
  features: API.Features;
  replyTime: string;
  externalAccounts: Account[];
  uatMicrodepAmntOne?: string;
  uatMicrodepAmntTwo?: string;
}

const Dashboard = ({
  androidUrl,
  appleUrl,
  institution,
  features,
  replyTime,
  externalAccounts,
  uatMicrodepAmntOne,
  uatMicrodepAmntTwo
}: DashboardProps) => {
  const { servicesApps, name } = institution;
  const { l } = useBreakpoints();
  const { accounts }: { accounts: Account[] } = useAccountsContext();
  const isDashboardLayoutRedesignEnabled = useDashboardLayoutRedesignEnabled();
  const {
    send: fetchSavvyMoneyRedirectUrl,
    error: hasSavvyWidgetErrored,
    loading: isSavvyFetchLoading
  } = entities.savvyMoney.useFetchSavvyMoneyRedirectUrl(
    entities.savvyMoney.SavvyMoneyViewUrl.WebWidget,
    Boolean(features.olb_enable_credit_monitor)
  );

  const isSubscriptionEnabled = useIsSubscriptionManagementEnabled();
  const { isLoading: areTransactionsLoading, settledTransactions } =
    useSettledTransactions(accounts);
  const settledTransactionsState = {
    settledTransactions,
    isLoading: areTransactionsLoading
  };

  useEffect(() => {
    fetchSavvyMoneyRedirectUrl();
  }, []);

  const navigate = useNavigate();
  const isDialogOpen = useIsDashboardDialogOpen();
  const { visibleLeftSectionIds, visibleRightSectionIds, visibleSections } =
    useVisibleWebDashboardSections({ externalAccounts, servicesApps });
  const sectionContext: DashboardSectionContext = {
    features,
    externalAccounts,
    uatMicrodepAmntOne,
    uatMicrodepAmntTwo,
    replyTime,
    settledTransactionsState,
    servicesApps,
    isSubscriptionEnabled,
    hasSavvyWidgetErrored,
    isSavvyFetchLoading,
    fetchSavvyMoneyRedirectUrl
  };

  const columnRatio = isDashboardLayoutRedesignEnabled
    ? "1fr 1fr"
    : "2fr minmax(380px, 1fr)";

  const pageLayoutClassName =
    l && isDashboardLayoutRedesignEnabled ? "layout-dual-column-dashboard" : "";

  const pageHeaderClassName = isDashboardLayoutRedesignEnabled
    ? "double-column-layout-header"
    : "";

  return (
    <>
      <PageLayout
        layout="dual-column"
        columnRatio={columnRatio}
        maxWidth="l"
        header={<DashboardHeader />}
        className={pageLayoutClassName}
        headerClass={pageHeaderClassName}
      >
        {l ? (
          <>
            {isDashboardLayoutRedesignEnabled ? (
              <LargeViewportLayout
                sections={visibleSections}
                externalAccounts={externalAccounts}
                servicesApps={servicesApps}
                sectionContext={sectionContext}
              />
            ) : (
              <>
                <div>
                  <OnboardingCard />
                  {visibleLeftSectionIds.flatMap((id) =>
                    renderLeftSection(id, sectionContext)
                  )}
                  <DashboardCustomization
                    externalAccounts={externalAccounts}
                    servicesApps={servicesApps}
                  />
                </div>
                <div>
                  {visibleRightSectionIds.flatMap((id) =>
                    renderRightSection(id, sectionContext)
                  )}
                  <NarmiTiles />
                </div>
              </>
            )}
          </>
        ) : (
          <>
            {/* Single column for tablets and phones */}
            <div>
              <OnboardingCard />

              {features.enable_dashboard_layout_redesign ? (
                <BalanceChart
                  settledTransactionsState={settledTransactionsState}
                />
              ) : (
                <AccountTotalsCard />
              )}
              {features.enable_dashboard_layout_redesign ? (
                <AccountsModule additionalExternalAccounts={externalAccounts} />
              ) : (
                <AccountBalances />
              )}
              <RecentTransactionsSection
                replyTime={replyTime}
                settledTransactionsState={settledTransactionsState}
              />
              {features.enable_dashboard_layout_redesign && (
                <QuickTransferCard />
              )}
              <NarmiTiles isDashboard={isDashboardLayoutRedesignEnabled} />
              {!features.enable_dashboard_layout_redesign &&
                (features.enable_external_accounts_on_dashboard ? (
                  <ExternalAccountsContainer
                    externalAccounts={externalAccounts}
                    uatMicrodepAmntOne={uatMicrodepAmntOne}
                    uatMicrodepAmntTwo={uatMicrodepAmntTwo}
                  />
                ) : (
                  <ExternalAccountTiles
                    externalAccounts={externalAccounts}
                    uatMicrodepAmntOne={uatMicrodepAmntOne}
                    uatMicrodepAmntTwo={uatMicrodepAmntTwo}
                  />
                ))}
              <MarketingCardContainer />
              <Feature features={features} or={["card_linked_offers"]}>
                <CardOffers />
              </Feature>
              <ChartCard
                borderCard={!!features.enable_dashboard_layout_redesign}
              />
              {features.enable_net_worth_widget &&
                features.enable_dashboard_layout_redesign && (
                  <NetWorthWidgetCard borderCard />
                )}
              {features.array_plus_credit_reporting && <ArrayPlusWidget />}
              {features.olb_enable_credit_monitor && (
                <SavvyMoneyWidget
                  hasErrored={hasSavvyWidgetErrored}
                  refetch={fetchSavvyMoneyRedirectUrl}
                  isLoading={isSavvyFetchLoading}
                  borderCard={!!features.enable_dashboard_layout_redesign}
                />
              )}
              {servicesApps && <ServicesCard services={servicesApps} />}
              {isSubscriptionEnabled && <SubscriptionCard />}
              <MarketingImage address={MARKETING_CARD_IMAGE_ADDRESS} />
            </div>
          </>
        )}
      </PageLayout>

      {/* Dialogs render outside PageLayout */}
      <DashboardDialog
        isOpen={isDialogOpen}
        fiName={name}
        androidUrl={androidUrl}
        appleUrl={appleUrl}
        closeDialog={() => navigate("/")}
      />
      <NpsSurvey />
    </>
  );
};

export default Dashboard;
